// STUB(instant-accounts) FILE: the whole file is stubbed data, removed by `node .claude/scripts/remove-stubs.mjs instant-accounts`
// Two fake Instant accounts (one ACTIVE, one BREACHED) shown first on the dashboard slider and the
// accounts page, with a fake details page. No backend is called for these ids.
import type {
  BasicChallenge,
  Challenge,
  ChallengeAddOn,
  ChallengeTrade,
  ChallengeTradesPaginated,
  PaginationPayload,
} from "@models";
import { Observable, of } from "rxjs";
import type { LegacyResponse } from "../../core/service/response.model";

const ACTIVE_ID: string = "stub-instant-active";
const BREACHED_ID: string = "stub-instant-breached";
const STUB_IDS: string[] = [ACTIVE_ID, BREACHED_ID];
const ACCOUNT_SIZE: number = 100000;

const STUB_TRADES: ChallengeTrade[] = [
  {
    tradeId: "stub-trade-1",
    positionId: "stub-position-1",
    action: "BUY",
    currency: "USD",
    symbol: "EURUSD",
    volume: 1.5,
    openTime: Date.parse("2026-10-20T08:15:00Z"),
    closeTime: Date.parse("2026-10-20T10:40:00Z"),
    openPrice: 1.2732,
    closePrice: 1.2775,
    commission: -7,
    swapFees: 0,
    profit: 645,
  },
  {
    tradeId: "stub-trade-2",
    positionId: "stub-position-2",
    action: "SELL",
    currency: "USD",
    symbol: "XAUUSD",
    volume: 0.4,
    openTime: Date.parse("2026-10-21T13:05:00Z"),
    closeTime: Date.parse("2026-10-21T14:20:00Z"),
    openPrice: 2681.4,
    closePrice: 2688.9,
    commission: -4,
    swapFees: 0,
    profit: -300,
  },
  {
    tradeId: "stub-trade-3",
    positionId: "stub-position-3",
    action: "BUY",
    currency: "USD",
    symbol: "NAS100",
    volume: 2,
    openTime: Date.parse("2026-10-22T14:30:00Z"),
    closeTime: Date.parse("2026-10-22T16:10:00Z"),
    openPrice: 20310.5,
    closePrice: 20342.0,
    commission: -10,
    swapFees: 0,
    profit: 630,
  },
];

// STUB(instant-addons) START: purchased add-ons of the active stub account, "No Minimum Trading Days" waives the Profitable Days objective
const ACTIVE_STUB_ADDONS: ChallengeAddOn[] = [
  {
    addonId: "stub-addon-profit-share",
    feeIncreaseRatio: 0.2,
    feeIncreaseFlat: 0,
    addonGroup: "INSTANT_ADDONS",
    addonActions: [
      { type: "PROFIT_SHARE", ratio: 1, description: "", appliesToPhases: ["FUNDED"] },
    ],
  },
  {
    addonId: "stub-addon-no-min-days",
    feeIncreaseRatio: 0.15,
    feeIncreaseFlat: 0,
    addonGroup: "CLASSIC_ADDONS",
    addonActions: [
      { type: "TRADING_DAYS_REDUCTION", ratio: 0, description: "", appliesToPhases: ["FUNDED"] },
    ],
  },
  {
    addonId: "stub-addon-fee-refund",
    feeIncreaseRatio: 0.1,
    feeIncreaseFlat: 0,
    addonGroup: "INSTANT_ADDONS",
    addonActions: [
      { type: "FEE_REFUND_SHARE", ratio: 1, description: "", appliesToPhases: ["FUNDED"] },
    ],
  },
  {
    addonId: "stub-addon-payout-frequency",
    feeIncreaseRatio: 0.05,
    feeIncreaseFlat: 0,
    addonGroup: "CLASSIC_ADDONS",
    addonActions: [
      {
        type: "PAYOUTS_FREQUENCY_INCREASE_2",
        ratio: 2,
        description: "",
        appliesToPhases: ["FUNDED"],
      },
    ],
  },
];

/** Hard-coded (untranslated) labels for stub add-ons that have no translation key yet. */
export const STUB_ADDON_LABELS: Record<string, string> = {
  "stub-addon-payout-frequency": "2x Payout Frequency",
};
// STUB(instant-addons) END

function buildStubAccount(id: string, accountNumber: string, isBreached: boolean): BasicChallenge {
  return {
    id,
    userId: "stub-user",
    planetName: "SATURN",
    accountNumber,
    subscriptionType: "FUNDED",
    accountSize: ACCOUNT_SIZE,
    currentBalance: isBreached ? 94200 : 101250,
    accountCurrency: "USD",
    startDate: isBreached ? "2026-10-02T09:00:00Z" : "2026-10-12T09:00:00Z",
    accountState: isBreached ? "BREACHED" : "ACTIVE",
    isDisplay: true,
    chargeSwapFees: false,
    user: { login: accountNumber, password: "stub-password" },
    type: "MT5",
    evaluationType: "INSTANT",
    labels: [],
  };
}

/** The two stub cards, first one active, second one breached. */
export const INSTANT_STUB_ACCOUNTS: BasicChallenge[] = [
  buildStubAccount(ACTIVE_ID, "9990001", false),
  buildStubAccount(BREACHED_ID, "9990002", true),
];

export function isInstantStubId(challengeId: string): boolean {
  return STUB_IDS.includes(challengeId);
}

/** Fake `GET /challenges/:id`, without the `instant` field so the detail page builds it itself. */
export function getInstantStubChallengeResponse$(
  challengeId: string,
): Observable<LegacyResponse<Challenge>> {
  const card: BasicChallenge =
    INSTANT_STUB_ACCOUNTS.find((account: BasicChallenge) => account.id === challengeId) ??
    INSTANT_STUB_ACCOUNTS[0];
  const isBreached: boolean = card.accountState === "BREACHED";

  const challenge: Challenge = {
    challengeId: card.id,
    accountNumber: card.accountNumber,
    planetName: card.planetName,
    subscriptionType: card.subscriptionType,
    chargeSwapFees: card.chargeSwapFees,
    accountState: card.accountState,
    visible: true,
    startDate: card.startDate,
    accountCurrency: card.accountCurrency,
    balance: card.currentBalance,
    netProfitLoss: card.currentBalance - ACCOUNT_SIZE,
    equity: isBreached ? card.currentBalance : card.currentBalance - 420,
    balanceAtStartOfDay: isBreached ? 96000 : 100800,
    maxPermittedLoss: ACCOUNT_SIZE * 0.05,
    dailyPermittedLoss: ACCOUNT_SIZE * 0.03,
    dailyProfit: isBreached ? -1800 : 450,
    minimumTradingDaysRequired: 5,
    minimumTradingDaysValue: 3,
    maxTodayTradedLossRequired: ACCOUNT_SIZE * 0.03,
    maxTodayTradedLossValue: 0,
    maxOverallTradedLossRequired: ACCOUNT_SIZE * 0.05,
    maxOverallTradedLossValue: 0,
    maxProfitTargetRequested: 0,
    maxProfitTargetValue: 0,
    openPositionsCount: isBreached ? 0 : 1,
    voucher: null,
    // STUB(instant-addons) START: active account has add-ons, the breached one has none
    addons: isBreached ? null : ACTIVE_STUB_ADDONS,
    // STUB(instant-addons) END
    type: card.type,
    userDetails: {
      login: card.accountNumber,
      password: "stub-password",
      investorPassword: "stub-investor",
      serverName: "Stub-Server",
    },
    initialAccountBalance: ACCOUNT_SIZE,
    equityAtStartOfDay: isBreached ? 96000 : 100800,
    accountLogin: card.accountNumber,
    accountPassword: "stub-password",
    evaluationType: "INSTANT",
    labels: [],
  };

  return of({ data: challenge, msg: "stub", status: 200 });
}

/** Fake `GET /trades/history/:id`, one page with the static rows. */
export function getInstantStubTrades$(
  paginationPayload: PaginationPayload,
): Observable<ChallengeTradesPaginated> {
  return of({
    items: STUB_TRADES,
    totalItems: STUB_TRADES.length,
    pageInfo: {
      currentPage: paginationPayload.page,
      size: paginationPayload.size,
      first: true,
      last: true,
      totalPages: 1,
    },
  });
}
